import { debugLog } from '../debug-log';
/**
 * LocalStorage-backed cache with TTL expiration.
 */

const CACHE_PREFIX = 'usage_cache_'; // Prefix for cache keys

interface CachedData<T> {
  value: T;
  timestamp: number;
  expiresIn: number; // milliseconds
}

/**
 * Save data to local storage as cache
 * @param key Cache key
 * @param data Data to save
 * @param expiresIn Expiration time (milliseconds). Default is 1 hour (3600000ms)
 */
export const saveCache = <T>(key: string, data: T, expiresIn: number = 3600000): boolean => {
  try {
    const cacheKey = `${CACHE_PREFIX}${key}`;
    const cachedData: CachedData<T> = {
      value: data,
      timestamp: Date.now(),
      expiresIn: expiresIn,
    };
    localStorage.setItem(cacheKey, JSON.stringify(cachedData));
    debugLog(`Cache saved: ${cacheKey}, expires in ${expiresIn}ms`);
    return true;
  } catch (error) {
    console.error(`Failed to save cache for key ${key}:`, error);
    return false;
  }
};

/**
 * Get cache data from local storage
 * @param key Cache key
 * @returns Cache data, or null if expired
 */
export const getCache = <T>(key: string): T | null => {
  try {
    const cacheKey = `${CACHE_PREFIX}${key}`;
    const stored = localStorage.getItem(cacheKey);
    if (!stored) {
      debugLog(`Cache not found: ${cacheKey}`);
      return null;
    }

    const cachedData: CachedData<T> = JSON.parse(stored);
    const now = Date.now();
    const age = now - cachedData.timestamp;

    if (age > cachedData.expiresIn) {
      // Cache has expired
      debugLog(`Cache expired: ${cacheKey}, age: ${Math.round(age / 1000)}s`);
      localStorage.removeItem(cacheKey);
      return null;
    }

    debugLog(`Cache hit: ${cacheKey}, age: ${Math.round(age / 1000)}s, expires in: ${Math.round((cachedData.expiresIn - age) / 1000)}s`);
    return cachedData.value;
  } catch (error) {
    console.error(`Failed to get cache for key ${key}:`, error);
    localStorage.removeItem(`${CACHE_PREFIX}${key}`); // Remove corrupted data
    return null;
  }
};

/**
 * Remove cache data from local storage
 * @param key Cache key
 */
export const removeCache = (key: string): boolean => {
  try {
    localStorage.removeItem(`${CACHE_PREFIX}${key}`);
    return true;
  } catch (error) {
    console.error(`Failed to remove cache for key ${key}:`, error);
    return false;
  }
};
